import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useReferenceData } from '../../shared/contexts/ReferenceDataContext';
import { createTemporaryReferenceData } from '../../pages/add-reference/utils/referenceNavigation';
import { toast } from 'sonner';

interface SharedFileData {
  name: string;
  type: string;
  size: number;
  data: ArrayBuffer;
}

interface SharedData {
  title?: string;
  text?: string;
  url?: string;
  files: File[];
}

interface StoredSharedData {
  id: string;
  title: string;
  text: string;
  url: string;
  files: SharedFileData[];
  timestamp: number;
  expiresAt: number;
}

interface DebugLog {
  timestamp: string;
  message: string;
  type: 'info' | 'error' | 'success';
}

interface ServiceWorkerLog {
  id: string;
  timestamp: number;
  message: string;
  type: 'info' | 'error' | 'success';
}

// Helper functions for IndexedDB operations
async function openShareDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('ShareTargetDB', 2); // Match service worker version
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('sharedData')) {
        db.createObjectStore('sharedData', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('swLogs')) {
        db.createObjectStore('swLogs', { keyPath: 'id' });
      }
    };
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error(request.error?.message || 'Database open failed'));
  });
}

async function getSharedData(db: IDBDatabase, id: string): Promise<StoredSharedData | null> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('sharedData', 'readonly');
    const store = transaction.objectStore('sharedData');
    const request = store.get(id);
    
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(request.error?.message || 'Get data failed'));
  });
}

async function deleteSharedData(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('sharedData', 'readwrite');
    const store = transaction.objectStore('sharedData');
    const request = store.delete(id);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(request.error?.message || 'Delete data failed'));
  });
}

async function getServiceWorkerLogs(db: IDBDatabase): Promise<ServiceWorkerLog[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('swLogs', 'readonly');
    const store = transaction.objectStore('swLogs');
    const request = store.getAll();
    
    request.onsuccess = () => {
      const logs = request.result as ServiceWorkerLog[];
      // Sort by timestamp, most recent first
      logs.sort((a, b) => b.timestamp - a.timestamp);
      resolve(logs);
    };
    request.onerror = () => reject(new Error(request.error?.message || 'Get logs failed'));
  });
}

async function clearServiceWorkerLogs(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('swLogs', 'readwrite');
    const store = transaction.objectStore('swLogs');
    const request = store.clear();
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(request.error?.message || 'Clear logs failed'));
  });
}

export function ShareTargetHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setTemporaryData } = useReferenceData();
  const [isProcessing, setIsProcessing] = useState(true);
  const [debugLogs, setDebugLogs] = useState<DebugLog[]>([]);
  const [swLogs, setSwLogs] = useState<ServiceWorkerLog[]>([]);
  const [showDebug, setShowDebug] = useState(true);

  const addDebugLog = useCallback((message: string, type: 'info' | 'error' | 'success' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`ShareTargetHandler [${timestamp}]: ${message}`);
  }, []);

  const loadServiceWorkerLogs = useCallback(async () => {
    try {
      const db = await openShareDatabase();
      const logs = await getServiceWorkerLogs(db);
      setSwLogs(logs);
      addDebugLog(`Loaded ${logs.length} service worker logs`);
    } catch (error) {
      addDebugLog(`Failed to load service worker logs: ${String(error)}`, 'error');
    }
  }, [addDebugLog]);

  useEffect(() => {
    // Load service worker logs first
    void loadServiceWorkerLogs();
  }, [loadServiceWorkerLogs]);

  useEffect(() => {
    const processSharedData = async () => {
      try {
        addDebugLog('Starting to process shared data');
        addDebugLog(`Current location: ${location.pathname}${location.search}`);
        
        const urlParams = new URLSearchParams(location.search);
        const shareId = urlParams.get('id');
        const error = urlParams.get('error');
        
        addDebugLog(`URL params - shareId: ${shareId}, error: ${error}`);
        
        // Handle share target errors
        if (error === 'share_failed') {
          addDebugLog('Share failed error detected', 'error');
          toast.error('Failed to process shared content. Please try again.');
          void navigate('/', { replace: true });
          return;
        }
        
        // Check if this is a new IndexedDB-based share (has shareId in URL)
        if (shareId) {
          addDebugLog(`Processing IndexedDB-based share with ID: ${shareId}`);
          // New IndexedDB-based approach
          try {
            addDebugLog('Opening IndexedDB...');
            const db = await openShareDatabase();
            addDebugLog('Database opened successfully', 'success');
            
            addDebugLog(`Retrieving data for ID: ${shareId}`);
            const storedData = await getSharedData(db, shareId);
            addDebugLog(`Retrieved data: ${storedData ? 'Found' : 'Not found'}`);
            
            if (!storedData) {
              addDebugLog('No data found for share ID', 'error');
              toast.error('Shared content not found or has expired');
              void navigate('/', { replace: true });
              return;
            }
            
            // Check if data has expired
            if (storedData.expiresAt && storedData.expiresAt < Date.now()) {
              addDebugLog('Data has expired', 'error');
              toast.error('Shared content has expired');
              // Clean up expired data
              await deleteSharedData(db, shareId);
              void navigate('/', { replace: true });
              return;
            }
            
            addDebugLog('Converting stored files to File objects...');
            // Convert stored file data back to File objects
            const files: File[] = [];
            if (storedData.files && storedData.files.length > 0) {
              addDebugLog(`Processing ${storedData.files.length} files`);
              for (const fileData of storedData.files) {
                try {
                  addDebugLog(`Converting file: ${fileData.name} (${fileData.type}, ${fileData.size} bytes)`);
                  const blob = new Blob([fileData.data], { type: fileData.type });
                  const file = new File([blob], fileData.name, { type: fileData.type });
                  files.push(file);
                  addDebugLog(`Successfully converted file: ${fileData.name}`, 'success');
                } catch (error) {
                  addDebugLog(`Failed to reconstruct file ${fileData.name}: ${String(error)}`, 'error');
                }
              }
            } else {
              addDebugLog('No files to process');
            }
            
            // Use the new unified data processing pipeline
            const tempData = createTemporaryReferenceData({
              title: storedData.title || undefined,
              description: storedData.text || undefined,
              sourceURL: storedData.url || undefined,
              imageBlob: files.length > 0 ? files[0] : undefined,
              initialImage: files.length > 0 ? URL.createObjectURL(files[0]) : undefined,
              source: 'web-share-target'
            });
            
            addDebugLog(`Prepared temporary data - title: ${tempData.title || 'none'}, description: ${tempData.description || 'none'}, sourceURL: ${tempData.sourceURL || 'none'}, hasImage: ${tempData.imageBlob ? 'yes' : 'no'}`);
            
            // Clean up the stored data after successful retrieval
            addDebugLog('Cleaning up stored data...');
            await deleteSharedData(db, shareId);
            addDebugLog('Data cleaned up successfully', 'success');
            
            // Store in context and navigate
            addDebugLog('Storing data in context and navigating...', 'success');
            setTemporaryData(tempData);
            
            // Add a small delay to let user see the debug info
            setTimeout(() => {
              void navigate('/new-reference', { replace: true });
            }, 3000); // Increased to 3 seconds to see both logs
            
          } catch (error) {
            addDebugLog(`Error retrieving shared data from IndexedDB: ${String(error)}`, 'error');
            toast.error('Failed to retrieve shared content. Please try again.');
            void navigate('/', { replace: true });
          }
        } else {
          addDebugLog('No share ID found, checking for legacy URL parameters...');
          // Legacy URL parameter approach (for backward compatibility)
          const source = urlParams.get('source');
          
          // If this was a legacy share target request, process the data
          if (source === 'share' || urlParams.get('title') || urlParams.get('text') || urlParams.get('url')) {
            addDebugLog('Processing legacy URL parameter share');
            
            // Use the new unified data processing pipeline for legacy data too
            const tempData = createTemporaryReferenceData({
              title: urlParams.get('title') || undefined,
              description: urlParams.get('text') || undefined,
              sourceURL: urlParams.get('url') || undefined,
              source: 'web-share-target'
            });

            addDebugLog(`Legacy temporary data: ${JSON.stringify(tempData)}`);

            // Store in context and navigate
            setTemporaryData(tempData);
            void navigate('/new-reference', { replace: true });
          } else {
            addDebugLog('No shared data found, redirecting to home');
            // If no shared data, redirect to home
            void navigate('/', { replace: true });
          }
        }
      } catch (error) {
        addDebugLog(`Error processing shared data: ${String(error)}`, 'error');
        toast.error('Failed to process shared content. Please try again.');
        void navigate('/', { replace: true });
      } finally {
        addDebugLog('Processing complete', 'success');
        setIsProcessing(false);
      }
    };

    void processSharedData();
  }, [navigate, location.search, location.pathname, addDebugLog, setTemporaryData]);

  const clearAllLogs = useCallback(async () => {
    try {
      const db = await openShareDatabase();
      await clearServiceWorkerLogs(db);
      setSwLogs([]);
      setDebugLogs([]);
      addDebugLog('All logs cleared', 'success');
    } catch (error) {
      addDebugLog(`Failed to clear logs: ${String(error)}`, 'error');
    }
  }, [addDebugLog]);

  if (isProcessing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-center p-8 bg-white rounded-lg shadow-sm max-w-4xl mx-4 w-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-4 text-gray-900">Processing shared content...</h2>
          <p className="text-gray-600 mb-6">Please wait while we prepare your reference.</p>
          
          {/* Debug Panel */}
          {showDebug && (
            <div className="mt-6 text-left">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium text-gray-800">Debug Information</h3>
                <div className="flex gap-2">
                  <button 
                    type="button"
                    onClick={() => void clearAllLogs()}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear Logs
                  </button>
                  <button 
                    type="button"
                    onClick={() => setShowDebug(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Hide
                  </button>
                </div>
              </div>
              
              {/* Service Worker Logs */}
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-700 mb-2">Service Worker Logs ({swLogs.length})</h4>
                <div className="bg-blue-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  {swLogs.length === 0 ? (
                    <p className="text-gray-500 text-sm">No service worker logs...</p>
                  ) : (
                    <div className="space-y-1">
                      {swLogs.slice(0, 20).map((log, index) => (
                        <div key={`sw-${log.id}`} className={`text-xs font-mono ${
                          log.type === 'error' ? 'text-red-600' : 
                          log.type === 'success' ? 'text-green-600' : 
                          'text-blue-700'
                        }`}>
                          <span className="text-gray-500">[{new Date(log.timestamp).toLocaleTimeString()}]</span> {log.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Component Logs */}
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-2">Component Logs ({debugLogs.length})</h4>
                <div className="bg-gray-100 rounded-lg p-4 max-h-48 overflow-y-auto">
                  {debugLogs.length === 0 ? (
                    <p className="text-gray-500 text-sm">No component logs yet...</p>
                  ) : (
                    <div className="space-y-1">
                      {debugLogs.map((log, index) => (
                        <div key={`${log.timestamp}-${index}`} className={`text-xs font-mono ${
                          log.type === 'error' ? 'text-red-600' : 
                          log.type === 'success' ? 'text-green-600' : 
                          'text-gray-700'
                        }`}>
                          <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {!showDebug && (
            <button 
              type="button"
              onClick={() => setShowDebug(true)}
              className="mt-4 text-sm text-blue-600 hover:text-blue-800"
            >
              Show Debug Information
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
} 